import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Row, Col } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaSave, FaArrowLeft } from 'react-icons/fa';
import categoryService from '../services/categoryService';
import CategoryTypeSelect from '../components/CategoryTypeSelect';

const CategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: ''
  });
  const [errors, setErrors] = useState({});

  const loadCategory = useCallback(async (categoryId) => {
    try {
      setLoading(true);
      const data = await categoryService.getById(categoryId);
      
      setFormData({
        name: data.name || '',
        type: data.type || ''
      });
    } catch (error) {
      console.error('Erro ao carregar categoria:', error);
      toast.error('Erro ao carregar dados da categoria. Tente novamente.');
      navigate('/categories');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (id) {
      loadCategory(id);
    }
  }, [id, loadCategory]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'O nome é obrigatório';
    }
    
    if (!formData.type) {
      newErrors.type = 'O tipo é obrigatório';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    try {
      setLoading(true);
      
      const categoryData = {
        name: formData.name.trim(),
        type: formData.type
      };
      
      if (id) {
        await categoryService.update(id, categoryData);
        toast.success('Categoria atualizada com sucesso!');
      } else {
        await categoryService.create(categoryData);
        toast.success('Categoria criada com sucesso!');
      }
      
      navigate('/categories');
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Erro ao salvar categoria. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <div className="sheet-container" data-testid="category-form">
        <div className="sheet-header">
          <h5 className="sheet-title">
            {id ? 'Editar Categoria' : 'Nova Categoria'}
          </h5>
          <div className="sheet-toolbar">
            <Button 
              variant="outline-secondary" data-testid="cancel-button"
              onClick={() => navigate('/categories')}
            >
              <FaArrowLeft className="me-1" /> Voltar
            </Button>
          </div>
        </div>
        
        <div className="sheet-content">
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nome *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name" data-testid="category-name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Digite o nome da categoria"
                    className={`excel-form-control ${errors.name ? 'is-invalid' : ''}`}
                    required
                  />
                  {errors.name && (
                    <Form.Control.Feedback type="invalid" data-testid="name-error">
                      {errors.name}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo *</Form.Label>
                  <CategoryTypeSelect
                    name="type" data-testid="category-type"
                    value={formData.type}
                    onChange={handleChange}
                    className={`excel-form-control ${errors.type ? 'is-invalid' : ''}`}
                    placeholder="Selecione o tipo da categoria"
                    required
                  />
                  {errors.type && (
                    <div className="invalid-feedback d-block" data-testid="type-error">
                      {errors.type}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            <div className="sheet-footer">
              <Button 
                variant="primary"
                type="submit" data-testid="save-button"
                disabled={loading}
              >
                <FaSave className="me-1" /> {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
};

export default CategoryForm;